import React, { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";
import BackspaceIcon from "@mui/icons-material/Backspace";
import { setEdgePin } from "../../ducks/authenticationDuck";
import { pincodeApi } from "../../actions/apiActions";

const KEYS = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  [null, "0", "backspace"],
];

const LocalPasswordDialog = ({ open, onClose, onSuccess, baseUrl }) => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const [pin, setPin] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const handleKey = useCallback((key) => {
    setErrorMsg("");
    if (key === "backspace") {
      setPin((prev) => prev.slice(0, -1));
    } else {
      setPin((prev) => prev + key);
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    if (pin.length === 0) return;
    setLoading(true);
    setErrorMsg("");
    try {
      await pincodeApi.validate(baseUrl, pin);
      dispatch(setEdgePin(pin));
      setPin("");
      onSuccess?.();
      onClose();
    } catch {
      setErrorMsg(t("Incorrect PIN"));
    } finally {
      setLoading(false);
    }
  }, [pin, baseUrl, dispatch, onSuccess, onClose, t]);

  const handleCancel = useCallback(() => {
    setPin("");
    setErrorMsg("");
    onClose();
  }, [onClose]);

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{t("Enter PIN")}</DialogTitle>
      <DialogContent>
        {errorMsg && <Alert severity="error" sx={{ mb: 1 }}>{errorMsg}</Alert>}
        <TextField
          fullWidth
          type="password"
          value={pin}
          slotProps={{ input: { readOnly: true } }}
          sx={{ mb: 2, mt: 1 }}
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "center" }}>
          {KEYS.map((row, rowIdx) => (
            <Box key={rowIdx} sx={{ display: "flex", gap: 1 }}>
              {row.map((key, colIdx) => {
                if (key === null) {
                  return <Box key={colIdx} sx={{ width: 64, height: 48 }} />;
                }
                if (key === "backspace") {
                  return (
                    <IconButton
                      key={colIdx}
                      onClick={() => handleKey("backspace")}
                      sx={{ width: 64, height: 48, borderRadius: 1, border: "1px solid", borderColor: "divider" }}
                    >
                      <BackspaceIcon />
                    </IconButton>
                  );
                }
                return (
                  <Button
                    key={colIdx}
                    variant="outlined"
                    onClick={() => handleKey(key)}
                    sx={{ width: 64, height: 48, fontSize: "1.2rem", minWidth: 0 }}
                  >
                    {key}
                  </Button>
                );
              })}
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={loading}>{t("Cancel")}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={pin.length === 0 || loading}>
          {loading ? <CircularProgress size={18} /> : t("OK")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LocalPasswordDialog;
